import { createApi } from '@reduxjs/toolkit/query/react';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { getBaseApiPath } from '@/app/common/api/config';
import { ApiResponse } from '@/app/common/types/common';
import { handleAiErrorResponse } from '@/data/ai_errors';
import {
  AiKeywordHistoryItem,
  DeleteAiKeywordsHistoryResponse,
  KeywordsDiffDto,
  SendAiKeywordResearchData,
  SendAiKeywordResearchRequest
} from '@/app/KeywordResearch/types/aiKeywordsApiTypes';

type EntityId = string | number;

const aiKeywordsApi = createApi({
  reducerPath: 'aiKeywords',
  tagTypes: ['AiKeywordsHistory'],
  baseQuery: (...args: unknown[]) =>
    customFetchBase(
      getBaseApiPath(),
      ...args
    ),
  endpoints: build => ({
    sendAiKeywordResearch: build.mutation<
      ApiResponse<SendAiKeywordResearchData>,
      SendAiKeywordResearchRequest
    >({
      query: (body: SendAiKeywordResearchRequest) => ({
        url: '/ai_binesh/mizfa_tools/v1/ai-related-keywords',
        method: 'POST',
        body
      }),
      invalidatesTags: ['AiKeywordsHistory'],
      transformErrorResponse: handleAiErrorResponse
    }),
    getAiKeywordsHistory: build.query<AiKeywordHistoryItem[], void>({
      query: () => ({
        url: '/ai_binesh/mizfa_tools/v1/ai-related-keywords/history',
        method: 'POST'
      }),
      providesTags: ['AiKeywordsHistory'],
      keepUnusedDataFor: 3,
      transformResponse: (response: ApiResponse<AiKeywordHistoryItem[]>) => response.data,
      transformErrorResponse: handleAiErrorResponse
    }),
    getAiKeywordsDetails: build.query<KeywordsDiffDto[], {id: EntityId}>({
      query: (body: {id: EntityId}) => ({
        url: '/ai_binesh/mizfa_tools/v1/ai-related-keywords/details',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<KeywordsDiffDto[]>) => response.data,
      transformErrorResponse: handleAiErrorResponse
    }),
    deleteAiKeywordsHistory: build.mutation<DeleteAiKeywordsHistoryResponse, {id: EntityId}>({
      query: (body: {id: EntityId}) => ({
        url: '/ai_binesh/mizfa_tools/v1/ai-related-keywords/history/delete',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['AiKeywordsHistory'],
      transformErrorResponse: handleAiErrorResponse
    })
  })
});

export const {
  useSendAiKeywordResearchMutation,
  useGetAiKeywordsHistoryQuery,
  useGetAiKeywordsDetailsQuery,
  useDeleteAiKeywordsHistoryMutation
} = aiKeywordsApi;

export default aiKeywordsApi;
